import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ArchiveRestore, Brain, ChevronDown, ChevronUp, History, Pencil, Pin, PinOff, Plus, Save, Trash2, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { apiGet, apiPatch, apiPost } from '../../lib/api'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'

type MemorySection = 'objective' | 'confirmed_decisions' | 'current_state' | 'artifacts' | 'completed_work' | 'open_questions' | 'cautions' | 'instructions'

interface MemoryItem {
  id: string
  section: MemorySection
  section_label: string
  content: string
  pinned: boolean
  hidden: boolean
  source_kind: 'summary' | 'user' | 'instruction' | 'correction'
  source_message_id?: string | null
  revision: number
  source_summary_revision?: number | null
  virtual: boolean
  origin: string
  updated_at?: string | null
}

interface MemoryEvent {
  id: string
  memory_item_id: string
  action: string
  previous_content?: string | null
  content?: string | null
  previous_pinned?: boolean | null
  pinned?: boolean | null
  created_at: string
}

interface MemoryResponse {
  items: MemoryItem[]
  removed_items: MemoryItem[]
  history: MemoryEvent[]
  pinned_count: number
}

const SECTION_OPTIONS: Array<{ value: MemorySection; label: string }> = [
  { value: 'instructions', label: 'Project instruction' },
  { value: 'objective', label: 'Objective' },
  { value: 'confirmed_decisions', label: 'Confirmed decision' },
  { value: 'current_state', label: 'Current configuration state' },
  { value: 'artifacts', label: 'Identifier or artifact' },
  { value: 'completed_work', label: 'Completed work' },
  { value: 'open_questions', label: 'Open question or next step' },
  { value: 'cautions', label: 'Constraint or caution' },
]

function eventLabel(event: MemoryEvent) {
  const labels: Record<string, string> = {
    created: 'Added memory', corrected: 'Corrected memory', pinned: 'Pinned memory',
    unpinned: 'Unpinned memory', removed: 'Removed memory', restored: 'Restored memory',
  }
  return labels[event.action] || event.action
}

export default function MemoryPanel({ conversationId }: { conversationId: string }) {
  const [data, setData] = useState<MemoryResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<{ item: MemoryItem; content: string } | null>(null)
  const [adding, setAdding] = useState(false)
  const [newSection, setNewSection] = useState<MemorySection>('instructions')
  const [newContent, setNewContent] = useState('')
  const [newPinned, setNewPinned] = useState(true)
  const [historyOpen, setHistoryOpen] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await apiGet(`/api/conversations/${conversationId}/memory`))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load memory')
    } finally {
      setLoading(false)
    }
  }, [conversationId])

  useEffect(() => { load() }, [load])

  const groups = useMemo(() => {
    const result = new Map<string, MemoryItem[]>()
    for (const item of data?.items || []) {
      const current = result.get(item.section_label) || []
      current.push(item)
      result.set(item.section_label, current)
    }
    return Array.from(result.entries())
  }, [data?.items])

  const createOverride = async (item: MemoryItem, changes: { content?: string; pinned?: boolean; hidden?: boolean }) => {
    await apiPost(`/api/conversations/${conversationId}/memory`, {
      section: item.section,
      content: changes.content ?? item.content,
      pinned: changes.pinned ?? item.pinned,
      hidden: changes.hidden ?? false,
      source_kind: 'correction',
      replaces_summary_section: true,
    })
  }

  const updateItem = async (item: MemoryItem, changes: { content?: string; pinned?: boolean; hidden?: boolean }) => {
    if (item.virtual) return createOverride(item, changes)
    await apiPatch(`/api/conversations/${conversationId}/memory/${item.id}`, {
      ...changes,
      expected_revision: item.revision,
    })
  }

  const mutate = async (operation: () => Promise<unknown>, success: string): Promise<boolean> => {
    setSaving(true)
    try {
      await operation()
      await load()
      toast.success(success)
      return true
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Memory update failed')
      return false
    } finally {
      setSaving(false)
    }
  }

  const saveEdit = async () => {
    if (!editing?.content.trim()) return
    const saved = await mutate(
      () => updateItem(editing.item, { content: editing.content.trim() }),
      'Memory corrected',
    )
    if (saved) setEditing(null)
  }

  const addMemory = async () => {
    if (!newContent.trim()) return
    const saved = await mutate(() => apiPost(`/api/conversations/${conversationId}/memory`, {
      section: newSection,
      content: newContent.trim(),
      pinned: newPinned,
      source_kind: newSection === 'instructions' ? 'instruction' : 'user',
      replaces_summary_section: false,
    }), 'Memory added')
    if (saved) {
      setAdding(false)
      setNewContent('')
      setNewPinned(true)
      setNewSection('instructions')
    }
  }

  if (loading && !data) return <div className="memory-panel-state"><Brain size={18} /><p>Loading durable memory…</p></div>
  if (error && !data) return <div className="memory-panel-state error"><Brain size={18} /><p>{error}</p><button onClick={load}>Try again</button></div>

  return (
    <div className="memory-panel">
      <div className="memory-panel-head">
        <div>
          <span className="workspace-kicker">Durable context</span>
          <h3>Agent memory</h3>
          <p>Correct this conversation's memory or pin facts that compaction must retain. The agent also receives corrected summaries from other conversations assigned to this connection.</p>
        </div>
        <Button size="sm" variant="accent" onClick={() => setAdding(value => !value)}>
          {adding ? <X size={12} /> : <Plus size={12} />} {adding ? 'Cancel' : 'Add'}
        </Button>
      </div>

      <div className="memory-safety-note">
        Edits change working context, not the transcript. Do not store passwords, tokens, or secret values here.
      </div>

      {adding && (
        <div className="memory-editor memory-add-editor">
          <label>Memory type</label>
          <select value={newSection} onChange={event => setNewSection(event.target.value as MemorySection)}>
            {SECTION_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <label>Concise instruction or fact</label>
          <Textarea value={newContent} onChange={event => setNewContent(event.target.value)} rows={4} maxLength={4000} placeholder="e.g. Never deploy this connection without a successful full connection test." />
          <label className="memory-pin-check"><input type="checkbox" checked={newPinned} onChange={event => setNewPinned(event.target.checked)} /> Pin for compaction</label>
          <div className="memory-editor-actions"><Button size="sm" onClick={addMemory} disabled={saving || !newContent.trim()}><Save size={12} /> Save memory</Button></div>
        </div>
      )}

      <div className="memory-summary-row">
        <span>{data?.items.length || 0} current items</span>
        <span><Pin size={11} /> {data?.pinned_count || 0} pinned</span>
      </div>

      {groups.length ? groups.map(([label, items]) => (
        <section className="memory-section" key={label}>
          <h4>{label}</h4>
          {items.map(item => (
            <article className={`memory-card ${item.pinned ? 'pinned' : ''}`} key={item.id}>
              {editing?.item.id === item.id ? (
                <div className="memory-editor">
                  <Textarea value={editing.content} onChange={event => setEditing({ item, content: event.target.value })} rows={5} maxLength={4000} autoFocus />
                  <div className="memory-editor-actions">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
                    <Button size="sm" onClick={saveEdit} disabled={saving || !editing.content.trim()}><Save size={12} /> Save correction</Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="memory-card-top">
                    <span>
                      {item.origin}
                      {item.source_summary_revision != null ? ` · summary r${item.source_summary_revision}` : ''}
                      {item.updated_at ? ` · ${new Date(item.updated_at).toLocaleDateString()}` : ''}
                    </span>
                    <div>
                      <button title={item.pinned ? 'Unpin memory' : 'Pin for compaction'} onClick={() => mutate(() => updateItem(item, { pinned: !item.pinned }), item.pinned ? 'Memory unpinned' : 'Memory pinned')} disabled={saving}>
                        {item.pinned ? <PinOff size={12} /> : <Pin size={12} />}
                      </button>
                      <button title="Correct memory" onClick={() => setEditing({ item, content: item.content })}><Pencil size={12} /></button>
                      <button title="Remove obsolete memory" onClick={() => mutate(() => updateItem(item, { hidden: true }), 'Memory removed')} disabled={saving}><Trash2 size={12} /></button>
                    </div>
                  </div>
                  <p>{item.content}</p>
                  <footer>
                    {item.pinned && <span className="memory-pinned-label"><Pin size={10} /> Retained during compaction</span>}
                    {item.source_message_id && <a href={`#message-${item.source_message_id}`}>View source message</a>}
                  </footer>
                </>
              )}
            </article>
          ))}
        </section>
      )) : (
        <div className="memory-empty"><Brain size={20} /><strong>No durable memory yet</strong><p>Memory appears after context compaction, or you can add a project instruction now.</p></div>
      )}

      <div className="memory-history">
        <button className="memory-history-toggle" onClick={() => setHistoryOpen(value => !value)}>
          <span><History size={13} /> Correction history</span>
          <span>{data?.history.length || 0} events {historyOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}</span>
        </button>
        {historyOpen && (
          <div className="memory-history-list">
            {(data?.removed_items || []).map(item => (
              <div className="memory-removed-item" key={item.id}>
                <div><strong>{item.section_label}</strong><p>{item.content || 'Compacted section removed as obsolete.'}</p></div>
                <Button variant="ghost" size="sm" onClick={() => mutate(() => updateItem(item, { hidden: false }), 'Memory restored')}><ArchiveRestore size={12} /> Restore</Button>
              </div>
            ))}
            {(data?.history || []).map(event => (
              <div className="memory-history-event" key={event.id}>
                <span>{eventLabel(event)}</span>
                <time>{new Date(event.created_at).toLocaleString()}</time>
                {event.action === 'corrected' && event.previous_content && <p>Previously: {event.previous_content}</p>}
              </div>
            ))}
            {!data?.history.length && !data?.removed_items.length && <p className="memory-history-empty">No user edits yet.</p>}
          </div>
        )}
      </div>
    </div>
  )
}
